import { z } from 'zod';

// `percent` is numeric(15,2) — Drizzle returns it as string by default; we
// expose it as string on the wire to avoid silent floating-point drift. The
// pricing engine already accepts string-coercible decimals via
// packages/shared/src/pricing/money.ts.
//
// This schema matches the shape of `vatTaxSettingSummarySchema` in
// packages/shared/src/schemas/products.ts intentionally — that summary stays
// embedded in the products.byId join while this slice owns the management
// CRUD surface. Deduping the two is an open follow-up.
export const vatTaxSettingItemSchema = z.object({
  id: z.uuid(),
  title: z.string().nullable(),
  percent: z.string().nullable(),
});

export type VatTaxSettingItem = z.infer<typeof vatTaxSettingItemSchema>;

const percentValue = z
  .union([z.string(), z.number()])
  .transform((v) => (typeof v === 'number' ? v.toString() : v))
  .refine((v) => /^\d+(\.\d{1,2})?$/.test(v), {
    message:
      'percent must be a non-negative decimal with up to 2 decimal places',
  });

export const createVatTaxSettingInput = z.object({
  title: z.string().min(1),
  percent: percentValue,
});

export type CreateVatTaxSettingInput = z.infer<typeof createVatTaxSettingInput>;

export const updateVatTaxSettingInput = z
  .object({
    id: z.uuid(),
    title: z.string().min(1).optional(),
    percent: percentValue.optional(),
  })
  .refine((v) => v.title !== undefined || v.percent !== undefined, {
    message: 'At least one of title or percent must be provided',
  });

export type UpdateVatTaxSettingInput = z.infer<typeof updateVatTaxSettingInput>;

export const findVatTaxSettingsInput = z.object({
  offset: z.number().int().min(0).optional(),
  limit: z.number().int().min(1).max(200).optional(),
  sort: z.enum(['title', 'percent', 'createdOn']).optional(),
  order: z.enum(['asc', 'desc']).optional(),
});

export type FindVatTaxSettingsInput = z.infer<typeof findVatTaxSettingsInput>;

export const findVatTaxSettingsResultSchema = z.object({
  data: z.array(vatTaxSettingItemSchema),
  count: z.number().int().nonnegative(),
  countAll: z.number().int().nonnegative(),
});

export type FindVatTaxSettingsResult = z.infer<
  typeof findVatTaxSettingsResultSchema
>;

export const deleteVatTaxSettingInput = z.object({
  id: z.uuid(),
});

export type DeleteVatTaxSettingInput = z.infer<typeof deleteVatTaxSettingInput>;

export const deleteVatTaxSettingResultSchema = z.object({
  id: z.uuid(),
});

export type DeleteVatTaxSettingResult = z.infer<
  typeof deleteVatTaxSettingResultSchema
>;
